import { useMemo } from "react";

/**
 * GoldSummaryPanel — sticky top panel showing aggregate gold weights across
 * all records in a Factory or Customer ledger:
 *   - Green box (right in RTL): sum of |net_gold| for records displayed in green (net_gold < 0).
 *   - Red box (left in RTL):    sum of net_gold for records displayed in red (net_gold > 0).
 *   - Top box: net = green − red. Tinted green if positive, red if negative.
 *
 *   Auto-updates whenever `items` changes — no extra fetch needed.
 */
export default function GoldSummaryPanel({ items, lang = "ar" }) {
    const { green, red, net } = useMemo(() => {
        let g = 0;
        let r = 0;
        (items || []).forEach((it) => {
            const v = Number(it?.net_gold) || 0;
            if (v > 0) r += v;
            else if (v < 0) g += Math.abs(v);
        });
        return { green: g, red: r, net: g - r };
    }, [items]);

    const fmt = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 3 });
    const netPositive = net > 0.0001;
    const netNegative = net < -0.0001;
    const netLabel = lang === "ar" ? "صافي الذهب (جرام)" : "Net Gold (g)";
    const greenLabel = lang === "ar" ? "إجمالي الأرقام الخضراء" : "Total Green";
    const redLabel = lang === "ar" ? "إجمالي الأرقام الحمراء" : "Total Red";

    return (
        <div className="mb-5 rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm sm:p-4" data-testid="gold-summary-panel">
            <div
                className={`rounded-xl px-4 py-3 text-center ring-1 ${
                    netPositive
                        ? "bg-emerald-50/70 ring-emerald-200"
                        : netNegative
                        ? "bg-red-50/70 ring-red-200"
                        : "bg-neutral-50 ring-neutral-200"
                }`}
                data-testid="gold-summary-net"
            >
                <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">{netLabel}</div>
                <div
                    className={`mt-1 font-numbers text-2xl font-bold sm:text-3xl ${
                        netPositive ? "text-emerald-700" : netNegative ? "text-red-700" : "text-neutral-500"
                    }`}
                    data-testid="gold-summary-net-value"
                >
                    {fmt(net)}
                </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
                {/* RTL → first child renders on the RIGHT (green per user's spec) */}
                <div className="rounded-xl bg-emerald-50/70 px-4 py-3 text-center ring-1 ring-emerald-200" data-testid="gold-summary-green">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{greenLabel}</div>
                    <div className="mt-1 font-numbers text-xl font-bold text-emerald-700" data-testid="gold-summary-green-value">
                        {fmt(green)}
                    </div>
                </div>
                <div className="rounded-xl bg-red-50/70 px-4 py-3 text-center ring-1 ring-red-200" data-testid="gold-summary-red">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-red-700">{redLabel}</div>
                    <div className="mt-1 font-numbers text-xl font-bold text-red-700" data-testid="gold-summary-red-value">
                        {fmt(red)}
                    </div>
                </div>
            </div>
        </div>
    );
}
